// ════════════════════════════════════════════════════════════════════
// SOA SIGN PAGE  (PUBLIC, UNAUTHENTICATED)
//
// Rendered when the URL matches /soa/<token>. Renders the canonical
// CMS MED-136 Scope of Sales Appointment Confirmation Form for the
// beneficiary to:
//   · Review the CMS disclosure body verbatim
//   · See the product checkboxes the agent pre-marked
//   · Optionally identify themselves as an authorized rep
//   · Sign on the canvas
//   · Submit
//
// On submit, /api/soa-client-sign captures signature + IP + UA and
// flips the row to status='pending_agent'. The agent gets the
// Health Portal banner alerting them to countersign.
// ════════════════════════════════════════════════════════════════════

import React from 'react';
import {
  SOA_DISCLOSURE, SOA_SIGNATURE_BLOCK, SOA_PRODUCT_CATALOG, SOA_PRODUCT_LABEL,
  SOA_RETENTION_NOTE, SOA_FORM_ID, formatSoaSignDateLong, deviceFingerprint, captureGeolocation,
} from '../lib/soa';

function SignaturePad({ onChange }) {
  const canvasRef = React.useRef(null);
  const drawingRef = React.useRef(false);
  const lastPoint = React.useRef(null);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width  = Math.floor(rect.width  * dpr);
    canvas.height = Math.floor(rect.height * dpr);
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.4; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0f172a';
  }, []);

  const ptFromEvent = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = (e.touches && e.touches[0]) || e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };
  const start = (e) => { e.preventDefault(); drawingRef.current = true; lastPoint.current = ptFromEvent(e); };
  const move  = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = ptFromEvent(e);
    ctx.beginPath(); ctx.moveTo(lastPoint.current.x, lastPoint.current.y); ctx.lineTo(p.x, p.y); ctx.stroke();
    lastPoint.current = p;
  };
  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    onChange && onChange(canvasRef.current.toDataURL('image/png'));
  };
  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.save(); ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height); ctx.restore();
    onChange && onChange('');
  };

  return (
    <div>
      <div
        style={{ touchAction: 'none', userSelect: 'none' }}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      >
        <canvas ref={canvasRef} className="block w-full bg-white border-2 border-slate-300" style={{ height: 200 }}/>
      </div>
      <div className="flex items-center justify-between mt-2 text-xs">
        <span className="text-slate-500 italic">Sign with your finger or mouse above</span>
        <button type="button" onClick={clear} className="text-slate-500 hover:text-slate-900 underline">Clear</button>
      </div>
    </div>
  );
}

export default function SoaSignPage({ token }) {
  const [phase, setPhase]   = React.useState('loading');
  const [errorMsg, setErrorMsg] = React.useState('');
  const [soa, setSoa]       = React.useState(null);
  const [signature, setSignature] = React.useState('');
  const [acked, setAcked]   = React.useState(false);
  const [isRep, setIsRep]   = React.useState(false);
  const [repName, setRepName] = React.useState('');
  const [repRelationship, setRepRelationship] = React.useState('');
  const [signResult, setSignResult] = React.useState(null);

  React.useEffect(() => {
    if (!token) { setPhase('error'); setErrorMsg('Missing SOA token.'); return; }
    fetch(`/api/soa-get?token=${encodeURIComponent(token)}`)
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || 'Link is invalid or has expired.');
        }
        return res.json();
      })
      .then((row) => { setSoa(row); setPhase('ready'); })
      .catch((err) => { setErrorMsg(err.message); setPhase('error'); });
  }, [token]);

  async function submit() {
    if (!signature || !acked) return;
    setPhase('signing');
    try {
      // We don't need geolocation on SOA — CMS doesn't require it,
      // and asking on a CMS form would feel out of place to the
      // beneficiary. Device fingerprint is captured.
      const fingerprint = await deviceFingerprint();
      const res = await fetch('/api/soa-client-sign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token, signature, fingerprint,
          rep_name: isRep ? repName : '',
          rep_relationship: isRep ? repRelationship : '',
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Unable to submit.');
      }
      const body = await res.json().catch(() => ({}));
      setSignResult(body);
      setPhase('done');
    } catch (err) {
      setErrorMsg(err.message);
      setPhase('error');
    }
  }

  // ── Loading / error / done shells ────────────────────────────
  if (phase === 'loading') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="text-slate-500 text-sm">Loading SOA…</div>
      </div>
    );
  }
  if (phase === 'error') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white border-2 border-rose-300 p-6 rounded">
          <div className="text-[0.65rem] font-black uppercase tracking-[0.25em] text-rose-700">Link Invalid</div>
          <h1 className="text-xl font-black text-slate-900 mt-2">This SOA link can't be opened.</h1>
          <p className="text-sm text-slate-600 mt-3">{errorMsg || 'The link is invalid, has expired, or has already been signed. Contact the agent who sent the link.'}</p>
        </div>
      </div>
    );
  }
  if (phase === 'done') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white border-2 border-emerald-400 p-6 rounded text-center">
          <div className="text-[0.65rem] font-black uppercase tracking-[0.25em] text-emerald-700">SOA Recorded</div>
          <h1 className="text-2xl font-black text-slate-900 mt-2">Thank you.</h1>
          <p className="text-sm text-slate-600 mt-3">Your Scope of Appointment has been recorded. Your agent will receive a notification and complete their portion. You can close this window.</p>
          <div className="mt-6 text-left bg-slate-50 border border-slate-200 p-3 text-sm">
            <div><strong>Signature Date:</strong> {formatSoaSignDateLong(signResult?.signed_at)}</div>
            {signResult?.ip && <div className="mt-1"><strong>IP Address:</strong> {signResult.ip}</div>}
          </div>
          <p className="text-[0.55rem] text-slate-400 italic mt-4 uppercase tracking-wider">{SOA_RETENTION_NOTE}</p>
          <p className="text-[0.55rem] text-slate-400 mt-1 uppercase tracking-wider">{SOA_FORM_ID}</p>
        </div>
      </div>
    );
  }

  // ── Ready (form) ─────────────────────────────────────────────
  const selectedProducts = new Set(soa.products || []);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-none mx-auto p-4 md:p-8 space-y-4">
        {/* Title — matches the CMS form heading typography */}
        <div className="bg-white border border-slate-300 px-6 py-5 text-center">
          <h1 className="text-2xl md:text-3xl font-black text-slate-900 tracking-tight">Scope of Sales Appointment Confirmation Form</h1>
        </div>

        {/* Disclosure body — verbatim CMS text */}
        <div className="bg-white border border-slate-300 p-5">
          <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">{SOA_DISCLOSURE}</p>
          <p className="text-sm font-bold text-slate-900 mt-3 text-center">Please mark beside the type of product(s) you want the agent to discuss.</p>
        </div>

        {/* Products — pre-checked by the agent, read-only (the beneficiary
            confirms by signing). Matches the canonical form layout —
            section headers + checkboxes with descriptive paragraphs. */}
        {SOA_PRODUCT_CATALOG.map(section => (
          <div key={section.section} className="bg-white border border-slate-300">
            <div className="bg-slate-100 border-b border-slate-300 px-4 py-2 font-bold text-slate-900">{section.section}</div>
            <div className="divide-y divide-slate-200">
              {section.items.map(p => {
                const checked = selectedProducts.has(p.id);
                return (
                  <div key={p.id} className="flex items-start gap-3 p-4">
                    <div className={`w-6 h-6 border-2 ${checked ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-400 bg-white'} flex items-center justify-center font-bold text-sm shrink-0 mt-0.5`}>
                      {checked ? '✓' : ''}
                    </div>
                    <div className="text-sm text-slate-800 leading-relaxed">
                      <strong>{p.label}</strong>
                      {p.description ? <> — {p.description}</> : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Sign-block body */}
        <div className="bg-white border border-slate-300 p-5">
          <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">{SOA_SIGNATURE_BLOCK}</p>
        </div>

        {/* Beneficiary identity (pre-filled) */}
        <div className="bg-white border border-slate-300 p-5">
          <h3 className="font-bold text-slate-900 mb-3 border-b border-slate-300 pb-2">Beneficiary or Authorized Representative</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            <div><strong>Name:</strong> {soa.beneficiary_name}</div>
            <div><strong>Phone:</strong> {soa.beneficiary_phone || '—'}</div>
            <div className="md:col-span-2"><strong>Address:</strong> {soa.beneficiary_address || '—'}</div>
          </div>

          <label className="flex items-center gap-2 mt-4 text-sm cursor-pointer">
            <input type="checkbox" checked={isRep} onChange={e => setIsRep(e.target.checked)} className="accent-slate-900 h-4 w-4"/>
            <span>I am signing as an authorized representative of the beneficiary above.</span>
          </label>

          {isRep && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
              <label className="block">
                <span className="text-[0.65rem] uppercase tracking-wider text-slate-500">Representative's Name</span>
                <input value={repName} onChange={e => setRepName(e.target.value)} className="mt-1 w-full border border-slate-300 px-2 py-2 text-sm"/>
              </label>
              <label className="block">
                <span className="text-[0.65rem] uppercase tracking-wider text-slate-500">Your Relationship to the Beneficiary</span>
                <input value={repRelationship} onChange={e => setRepRelationship(e.target.value)} className="mt-1 w-full border border-slate-300 px-2 py-2 text-sm"/>
              </label>
            </div>
          )}
        </div>

        {/* Acknowledgement + signature */}
        <label className="flex items-start gap-3 bg-white border border-slate-300 p-4">
          <input type="checkbox" checked={acked} onChange={e => setAcked(e.target.checked)} className="mt-1 accent-slate-900 h-5 w-5 shrink-0"/>
          <span className="text-sm text-slate-800">
            I have read, understood, and agree to the products I want the agent to discuss as marked above.
          </span>
        </label>

        <div className="bg-white border border-slate-300 p-5">
          <h3 className="font-bold text-slate-900 mb-3">Signature:</h3>
          <SignaturePad onChange={setSignature}/>
        </div>

        <button
          onClick={submit}
          disabled={!signature || !acked || phase === 'signing'}
          className="w-full bg-slate-900 hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed text-white font-black uppercase tracking-tight py-3"
        >
          {phase === 'signing' ? 'Submitting…' : 'Sign + Submit'}
        </button>

        {/* Agent block (read-only — printed on the canonical form) */}
        <div className="bg-white border border-slate-300 p-5">
          <h3 className="font-bold text-slate-900 mb-3 border-b border-slate-300 pb-2">To Be Completed By Agent</h3>
          <div className="text-sm text-slate-800 space-y-1">
            <div><strong>Agent Name:</strong> {soa.agent_name}</div>
            <div><strong>Agent NPN:</strong> {soa.agent_npn}</div>
            <div><strong>Initial Method of Contact:</strong> {soa.contact_method || '—'}</div>
            <div className="text-slate-400 italic text-xs mt-2">The agent will sign + complete their portion after you submit.</div>
          </div>
        </div>

        <div className="text-[0.6rem] text-slate-400 italic mt-4 text-center uppercase tracking-wider">
          {SOA_RETENTION_NOTE}
        </div>
        <div className="text-[0.55rem] text-slate-300 text-center font-mono">
          {SOA_FORM_ID}
        </div>
      </div>
    </div>
  );
}
